import React from 'react';
import _ from 'lodash';

import {
  styled,
  Typography,
  Grid,
  Button
} from '@mui/material';
import {
  authService,
  ContainerBox,
  fields,
  Form,
  LoadingOverlay,
  ResponsiveImage
} from 'iron-forge-react-modules';
import {
  activityTemplates,
  appFormUtil,
  routeDirectory
} from 'modules';

import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as types from './types';

import authImage1x from 'assets/images/authImage/1x.png';
import authImage2x from 'assets/images/authImage/2x.png';
import authImage3x from 'assets/images/authImage/3x.png';



const TextFieldController = fields.TextField.genControllerComponent<types.IFormState>();

const HeaderTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4)
}));

const DescriptionTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3)
}));

const ErrorTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.light
}));

const FormGrid = styled(Grid)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '327px',
  marginLeft: 'auto',
  marginRight: 'auto'
});

type IResetPasswordStep = 'submitEmail' | 'resetPassword' | 'success';



const __step2DescriptionText: Record<IResetPasswordStep, string> = {
  submitEmail: "Enter your email in order to send a password reset email",
  resetPassword: "If you have an account with our system, you will have received an email containing a verification code. Enter that below.",
  success: "Success! Log in with your email and new password by pressing the button below."
};



export const ForgotPasswordActivity: React.FC = () => {
  const navigate = useNavigate();
  const form = useForm<types.IFormState>({
    defaultValues: {
      email: '',
      code: '',
      password: ''
    }
  });
  const [loading, setLoading] = React.useState(false);
  const [resetPasswordStep, setResetPasswordStep] = React.useState<IResetPasswordStep>('submitEmail');
  const [errorMessage, setErrorMessage] = React.useState('');



  async function processSubmit(formState: types.IFormState) {
    setLoading(true);
    const {
      email,
      password,
      code
    } = appFormUtil.trimFormStateValues(formState);
    if (resetPasswordStep === 'submitEmail') {
      await authService.forgotPassword(email);
      setResetPasswordStep('resetPassword');

    } else if (resetPasswordStep === 'resetPassword') {
      try {
        await authService.forgotPasswordSubmit(code, password);
        setResetPasswordStep('success');

      } catch (err) {
        if (err instanceof Error) {
          setErrorMessage(err.message);
        }
      }
    }

    setLoading(false);
  }



  function handleClickSignIn() {
    navigate(routeDirectory.auth.logIn.path(), {
      replace: true
    });
  }



  return (
    <activityTemplates.BaseActivityTemplate.FC onlyAllowLoggedOutUsers>
      <LoadingOverlay.FC active={loading} />

      <Grid container>
        <Grid item>
          <ResponsiveImage.FC urlSet={[
            authImage1x,
            authImage2x,
            authImage3x
          ]} />
        </Grid>

        <FormGrid item>
          <Form.FC onSubmit={form.handleSubmit(processSubmit)}>
            <ContainerBox.FC>
              <HeaderTypography variant="h2">
                {resetPasswordStep === 'success' ? "Password reset!" : "Forgot Password"}
              </HeaderTypography>

              <DescriptionTypography>
                {__step2DescriptionText[resetPasswordStep]}
              </DescriptionTypography>

              {!_.isEmpty(errorMessage) && (
                <ErrorTypography>
                  {errorMessage}
                </ErrorTypography>
              )}

              {resetPasswordStep === 'submitEmail' && (
                <TextFieldController
                  isRequired
                  formControl={form.control}
                  isDisabled={loading}
                  label="Email"
                  fieldKey="email"
                  type="email"
                  size="small" />
              )}

              {resetPasswordStep === 'resetPassword' && (
                <>
                  <TextFieldController
                    isRequired
                    formControl={form.control}
                    isDisabled={loading}
                    label="Verification Code"
                    fieldKey="code"
                    size="small" />

                  <TextFieldController
                    isRequired
                    formControl={form.control}
                    isDisabled={loading}
                    label="New Password"
                    fieldKey="password"
                    size="small"
                    type="password" />
                </>
              )}

              {resetPasswordStep === 'success' && (
                <Button
                  variant="contained"
                  onClick={handleClickSignIn}>
                  Log In
                </Button>
              )}

              {resetPasswordStep !== 'success' && (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}>
                  Submit
                </Button>
              )}
            </ContainerBox.FC>
          </Form.FC>
        </FormGrid>
      </Grid>

    </activityTemplates.BaseActivityTemplate.FC>
  );
};
